import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Container, Image } from 'react-bootstrap'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Header from '../../components/layout/header/Header'
import Button from '../../components/shared/buttons/button'
import UploadPrescriptionModal from '../../components/shared/modalContent/UploadPrescriptionModal'
import PrescriptionList from './PrescriptionList'
import LensImage from '../../images/lensImage.png'
import { ManualSvg } from '../../svgFiles/ManualSvg'
import { UploadFileSvg } from '../../svgFiles/UploadFileSvg'
import { CircleCheckSvg } from '../../svgFiles/CircleCheckSvg'
import { UndoSvg } from '../../svgFiles/UndoSvg'

import {
  useGetPrescriptionQuery,
  useUpdatePrescriptionMutation,
} from '../../app/prescriptionApi/prescriptionApi'
import { useGetodersByFilterMutation } from '../../app/orderApi/OrderApi'
import './prescription.css'
import { showToast } from '../../helpers/Utils';


// Default form values
const INITIAL_FORM_VALUES = {
  ItemID: null,
  prescriptionType: null,
  PDOneorTwoNumbers: null,
  SingularPDValue: null,
  ODRightSphere_SPH: null,
  ODRightCylinder_CYL: null,
  ODRightAxis: null,
  ODRightPrism: '',
  ODRightADD: null,
  ODRightPupillaryDistance_PD: null,
  OSLeftSphere_SPH: null,
  OSLeftCylinder_CYL: null,
  OSLeftAxis: null,
  OSLeftPrism: '',
  OSLeftADD: null,
  OSLeftPupillaryDistance_PD: null,
}

const REQUIRED_FIELDS = [
  'ODRightSphere_SPH',
  'ODRightCylinder_CYL',
  'ODRightAxis',
  'OSLeftSphere_SPH',
  'OSLeftCylinder_CYL',
  'OSLeftAxis',
]

const Prescription = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const userItemID = searchParams.get("id")?.replace(/ /g, "+")

  // ✅ use mutation instead of query
  const [getOrders, { data: userData, isLoading: isUserDataLoading }] = useGetodersByFilterMutation();
  const orders = userData?.data?.orders || [];

  // Filter only orders where ProductType is "lens" and prescriptionMethod is "Email Later"
  const lensOrders = orders.filter(order => 
    order.ProductType === "lens" && order.prescriptionMethod === "Email Later"
  );
  const isFieldShow = lensOrders?.length > 0;

  // trigger fetch manually when ItemID changes
  useEffect(() => {
    if (userItemID) {
      getOrders({ ShopifyId: userItemID, page: 1 })
    }
  }, [userItemID, getOrders])

  // State for each lens item - keyed by ItemID
  const [formValuesMap, setFormValuesMap] = useState({})
  const [selectedOptionMap, setSelectedOptionMap] = useState({})
  const [showUploadModalMap, setShowUploadModalMap] = useState({})
  const [uploadedFileMap, setUploadedFileMap] = useState({})
  const [errorsMap, setErrorsMap] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Get unique prescription types from lens orders
  const prescriptionTypes = useMemo(
    () => [...new Set(lensOrders.map(order => order.prescriptionType).filter(Boolean))],
    [lensOrders]
  )

  // Fetch prescription data for the first prescription type (assuming all are same type)
  const { data: prescriptionData, isLoading } = useGetPrescriptionQuery(
    { prescriptionType: prescriptionTypes[0] },
    { skip: !prescriptionTypes[0] || prescriptionTypes[0] === "I Don't Need Prescription Lenses", })

  const [updatePrescription] = useUpdatePrescriptionMutation()

  // Initialize form values for each lens item
  useEffect(() => {
    if (lensOrders?.length) {
      const newFormValuesMap = {}
      const newSelectedOptionMap = {}
      
      lensOrders.forEach(order => {
        const itemId = order.ItemID
        newFormValuesMap[itemId] = {
          ...INITIAL_FORM_VALUES,
          prescriptionType: order?.prescriptionType || 'Progressive Lenses',
          ItemID: itemId,
          PDOneorTwoNumbers: order?.PDOneorTwoNumbers || undefined,
          SingularPDValue: order?.SingularPDValue || undefined,
          ODRightSphere_SPH: order?.ODRightSphere_SPH || undefined,
          ODRightCylinder_CYL: order?.ODRightCylinder_CYL || undefined,
          ODRightAxis: order?.ODRightAxis ?? undefined,
          ODRightPrism: order?.ODRightPrism || '',
          ODRightADD: order?.ODRightADD || undefined,
          ODRightPupillaryDistance_PD: order?.ODRightPupillaryDistance_PD || undefined,
          OSLeftSphere_SPH: order?.OSLeftSphere_SPH || undefined,
          OSLeftCylinder_CYL: order?.OSLeftCylinder_CYL || undefined,
          OSLeftAxis: order?.OSLeftAxis ?? undefined,
          OSLeftPrism: order?.OSLeftPrism || '',
          OSLeftADD: order?.OSLeftADD || undefined,
          OSLeftPupillaryDistance_PD: order?.OSLeftPupillaryDistance_PD || undefined,
        }
        // No default — user must pick One Number or Two Numbers
        newSelectedOptionMap[itemId] = order?.PDOneorTwoNumbers || null
      })
      
      setFormValuesMap(newFormValuesMap)
      setSelectedOptionMap(newSelectedOptionMap)
    }
  }, [userData, userItemID])

  // Validation logic for a specific item
  const validateAll = useCallback(() => {
    let isValid = true
    const newErrorsMap = {}

    lensOrders.forEach((order) => {
      const itemId = order.ItemID
      const formValues = formValuesMap[itemId] || {}
      const uploadedFile = uploadedFileMap[itemId]
      const errs = {}

      if (!uploadedFile) {
        REQUIRED_FIELDS.forEach((field) => {
          const value = formValues[field]
          if (value === null || value === undefined || value === "") {
            errs[field] = true
          }
        })

        if (formValues.prescriptionType === "Progressive Lenses") {
          if (!formValues.ODRightADD) errs.ODRightADD = true
          if (!formValues.OSLeftADD) errs.OSLeftADD = true
        }

        // PD One/Two + PD values are optional — blank PD is allowed
        ["ODRightAxis", "OSLeftAxis"].forEach((axis) => {
          const val = formValues[axis];
          if (val === null || val === undefined || val === "") {
            errs[axis] = true;
          }
        });

        if (Object.keys(errs).length > 0) {
          newErrorsMap[itemId] = errs
          isValid = false
        }
      }
    })

    setErrorsMap(newErrorsMap)
    return isValid
  }, [lensOrders, formValuesMap, uploadedFileMap])

  const handleSelect = useCallback((itemId, field, value) => {
    const val = value?.value ?? value
    const finalVal = typeof val === 'string' && !isNaN(Number(val)) ? Number(val) : val
    setFormValuesMap(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], [field]: finalVal }
    }))
    setErrorsMap(prev => {
      const itemErrors = prev[itemId] || {}
      const { [field]: _, ...rest } = itemErrors
      return { ...prev, [itemId]: rest }
    })
  }, [])

  const handleSubmitAll = async () => {
    if (!validateAll()) {
      showToast("Please fill all required fields.", "error")
      return
    }

    setIsSubmitting(true)

    try {
      const promises = lensOrders.map(async (order) => {
        const itemId = order.ItemID
        const formValues = formValuesMap[itemId] || {}
        const selectedOption = selectedOptionMap[itemId] || null
        const uploadedFile = uploadedFileMap[itemId] || null

        const formData = new FormData()
        formData.append("ItemID", itemId)

        if (uploadedFile) {
          formData.append("prescriptionType", formValues.prescriptionType)
          formData.append("prescriptionUpload", uploadedFile)
        } else {
          Object.entries(formValues).forEach(([k, v]) => {
            if (
              v != null &&
              v !== "" &&
              k !== "ItemID" &&
              !k.includes("PD") &&
              !k.includes("SingularPD")
            ) {
              formData.append(k, v)
            }
          })

          if (selectedOption) {
            formData.set("PDOneorTwoNumbers", selectedOption)
          }

          if (selectedOption === "One") {
            if (
              formValues.SingularPDValue != null &&
              formValues.SingularPDValue !== ""
            ) {
              formData.append(
                "SingularPDValue",
                Number(formValues.SingularPDValue)
              )
            }
          } else if (selectedOption === "Two") {
            if (
              formValues.ODRightPupillaryDistance_PD != null &&
              formValues.ODRightPupillaryDistance_PD !== ""
            ) {
              formData.append(
                "ODRightPupillaryDistance_PD",
                Number(formValues.ODRightPupillaryDistance_PD)
              )
            }
            if (
              formValues.OSLeftPupillaryDistance_PD != null &&
              formValues.OSLeftPupillaryDistance_PD !== ""
            ) {
              formData.append(
                "OSLeftPupillaryDistance_PD",
                Number(formValues.OSLeftPupillaryDistance_PD)
              )
            }
          }
        }

        return updatePrescription({ body: formData }).unwrap()
      })

      await Promise.all(promises)
      showToast("All prescriptions submitted successfully!", "success")

      navigate("/confirmation")
    } catch (err) {
      showToast(err.data?.message || "Failed to save prescriptions", "error")
      console.error("Update failed:", err)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading || isUserDataLoading) {
    return null;
  }


  //  If item does not need prescription, show message and stop
  if (!isFieldShow) {
    return (
      <div className="userWrapper">
        <Header />
        <Container className="presc-container text-center">
          <h2>This item does not require a prescription.</h2>
        </Container>
      </div>
    )
  }


  return (
    <div className="userWrapper">
      <Header />
      <Container className="presc-container">
        <h1 className="cpt-title">Enter your prescription</h1>
        <p className="presc-desc">
          Your prescription will be sent straight to the lens lab after submission.
          <br />Questions?{" "}
          <a href="mailto:support@captify.glass" className="hilight">
            support@captify.glass
          </a>
        </p>

        {lensOrders.map((order, index) => {
          const itemId = order.ItemID
          const formValues = formValuesMap[itemId] || INITIAL_FORM_VALUES
          const selectedOption = selectedOptionMap[itemId] || null
          const uploadedFile = uploadedFileMap[itemId] || null
          const errors = errorsMap[itemId] || {}
          const showUploadModal = showUploadModalMap[itemId] || false

          return (
            <div key={itemId} className="presc-card mb-4">
              <div className="d-flex gap-3 justify-content-between w-100 presc-header">
                <div className="d-flex align-items-center gap-3 presc-product-heading">
                  <Image src={LensImage} alt="lens" />
                  <div>
                    <h3 className="mb-0">{order?.SkuName}</h3>
                    {order?.CaptifyVersion && (
                      <p className="mb-0 text-muted small">Captify Version: {order.CaptifyVersion}</p>
                    )}
                  </div>
                </div>
                <Button
                  className="uploadButton desktopOnly"
                  onClick={() => setShowUploadModalMap(prev => ({ ...prev, [itemId]: true }))}
                  label="Or Upload Prescription File"
                  icon={<UploadFileSvg />}
                />
              </div>

              <UploadPrescriptionModal
                show={showUploadModal}
                setShow={(show) => setShowUploadModalMap(prev => ({ ...prev, [itemId]: show }))}
                setUploadedFile={(file) => setUploadedFileMap(prev => ({ ...prev, [itemId]: file }))}
              />

              {!uploadedFile && (
                <PrescriptionList
                  listData={prescriptionData}
                  selectedOption={selectedOption}
                  setSelectedOption={(option) => setSelectedOptionMap(prev => ({ ...prev, [itemId]: option }))}
                  handleSelect={(field, value) => handleSelect(itemId, field, value)}
                  formValues={formValues}
                  errors={errors}
                  currentOrder={order}
                />
              )}

              {!uploadedFile ? (
                <div className="mt-3 d-flex justify-content-center">
                  <Button
                    className="uploadButton mobileOnly"
                    onClick={() => setShowUploadModalMap(prev => ({ ...prev, [itemId]: true }))}
                    label="Or Upload Prescription File"
                    icon={<UploadFileSvg />}
                  />
                </div>
              ) : (
                <div className="d-flex flex-column align-items-center text-center gap-2 presec-added">
                  <CircleCheckSvg className="text-success" />
                  <p className="text-success">
                    File: <strong>{uploadedFile.name}</strong> uploaded successfully!
                  </p>
                  <div className="d-flex gap-3 justify-content-center">
                    <Button
                      onClick={() => {
                        setUploadedFileMap(prev => ({ ...prev, [itemId]: null }))
                        setShowUploadModalMap(prev => ({ ...prev, [itemId]: true }))
                      }}
                      icon={<UndoSvg />}
                      size="sm"
                      label="Re-upload"
                      className="cancel bg-white cancel-upload"
                    />
                    <Button
                      onClick={() => setUploadedFileMap(prev => ({ ...prev, [itemId]: null }))}
                      icon={<ManualSvg />}
                      size="sm"
                      label="Fill in manually"
                      className="cancel bg-white cancel-upload"
                    />
                  </div>
                </div>
              )}

              {Object.keys(errors).length > 0 && (
                <p className="text-danger">Please fill all required fields.</p>
              )}
            </div>
          )
        })}

        <div className="text-center mt-4 mb-5">
          <Button
            label={isSubmitting ? "Submitting..." : "Submit All"}
            size="xxl"
            onClick={handleSubmitAll}
            disabled={isSubmitting}
          />
        </div>
      </Container>
    </div>
  )
}

export default Prescription
